import { useState, useRef, useEffect } from 'react';
import { API_URL } from '../config';

const INDEX_NAME = 'podcast_counseling_stefano';
const SYSTEM_PROMPT = `Sei un assistente esperto di counseling basato sui contenuti del podcast "Libri Schermi Counseling" di Stefano De Sanctis.
Rispondi in italiano in modo chiaro e professionale. Basa le tue risposte esclusivamente sui contenuti del podcast.
Quando pertinente, cita l'episodio di riferimento. Se non trovi informazioni rilevanti nei contenuti, dillo chiaramente.
Non inventare mai informazioni non presenti nei testi del podcast.`;

/** Default impostazioni pipeline RAG (come nel modello locale) */
const DEFAULT_RAG_SETTINGS = {
  spellCheck: true,
  acronymExpansion: true,
  multiQuery: true,
  hyde: false,
  reranking: true,
  queryCache: true,
  topK: 8,
  temperature: 0.4,
  maxTokens: 1024,
  cacheTtlSec: 30 * 60,
  cacheSize: 500,
};

interface Message {
  role: 'user' | 'assistant';
  content: string;
  sources?: Array<{ source: string; score: number }>;
}

export default function StefanoPage() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showProfile, setShowProfile] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [ragSettings, setRagSettings] = useState(DEFAULT_RAG_SETTINGS);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    const question = input.trim();
    if (!question || isLoading) return;

    setInput('');
    setShowProfile(false);
    setMessages(prev => [...prev, { role: 'user', content: question }]);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/flow/kb/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: question,
          index_name: INDEX_NAME,
          system_prompt: SYSTEM_PROMPT,
          top_k: ragSettings.topK,
          embed_model: 'BAAI/bge-m3',
          llm_model: 'llama-3.3-70b-versatile',
          temperature: ragSettings.temperature,
          max_tokens: ragSettings.maxTokens,
          use_spell_check: ragSettings.spellCheck,
          use_acronym_expansion: ragSettings.acronymExpansion,
          use_multi_query: ragSettings.multiQuery,
          use_hyde: ragSettings.hyde,
          use_reranking: ragSettings.reranking,
          use_query_cache: ragSettings.queryCache,
          cache_ttl_sec: ragSettings.cacheTtlSec,
          cache_size: ragSettings.cacheSize,
        }),
      });

      if (!response.ok) {
        const msg = response.status === 404
          ? 'Endpoint chat non trovato (404). In produzione verifica che il backend pigna-studio sia raggiungibile e che VITE_API_URL punti al backend.'
          : `HTTP ${response.status}`;
        throw new Error(msg);
      }
      if (!response.body) throw new Error('No response body');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let sources: Array<{ source: string; score: number }> = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        let currentEvent = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) currentEvent = line.slice(7).trim();
          else if (line.startsWith('data: ') && currentEvent) {
            try {
              const data = JSON.parse(line.slice(6));
              if (currentEvent === 'sources' && data.sources) {
                sources = data.sources;
                setMessages(prev => [...prev, { role: 'assistant', content: '', sources }]);
              } else if (currentEvent === 'message' && data.chunk) {
                setMessages(prev => {
                  const next = [...prev];
                  const last = next[next.length - 1];
                  if (last?.role === 'assistant') {
                    next[next.length - 1] = { ...last, content: last.content + data.chunk };
                  }
                  return next;
                });
              } else if (currentEvent === 'error' && data.error) {
                setMessages(prev => [...prev, { role: 'assistant', content: `Errore: ${data.error}`, sources: [] }]);
              }
            } catch (_) {}
            currentEvent = '';
          }
        }
      }
    } catch (error: any) {
      console.error('Errore chat:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Si è verificato un errore nella comunicazione con il server. Riprova tra poco.',
      }]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const suggestedQuestions = [
    'Quali sono le emozioni base nel counseling?',
    'Come funziona il counseling breve di Littrell?',
    'Come gestire il tradimento nella coppia?',
    'Cos\'è il feedback fenomenologico?',
    'Cosa sono le polarità nel counseling?',
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-lg">
              S
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Stefano De Sanctis</h1>
              <p className="text-xs text-slate-500">Podcast Counseling - AI Assistant</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setShowSettings(true)}
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-indigo-600 transition-colors"
              title="Impostazioni pipeline RAG"
              aria-label="Impostazioni"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            <button
              onClick={() => setShowProfile(!showProfile)}
              className="text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
            >
              {showProfile ? 'Chiudi profilo' : 'Profilo'}
            </button>
          </div>
        </div>
      </header>

      {/* Modal impostazioni pipeline RAG */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowSettings(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-slate-200" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-lg font-bold text-slate-900">Pipeline RAG</h2>
              <button type="button" onClick={() => setShowSettings(false)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700">✕</button>
            </div>
            <div className="px-6 py-5 space-y-6">
              <section>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Moduli pipeline (attivi per ogni query)</h3>
                <ul className="space-y-3">
                  {[
                    { key: 'spellCheck' as const, label: 'Spell Check', desc: 'Correzione ortografica trigram + Levenshtein' },
                    { key: 'acronymExpansion' as const, label: 'Espansione Acronimi', desc: 'Riconosce e espande acronimi comuni' },
                    { key: 'multiQuery' as const, label: 'Multi-Query', desc: 'Genera varianti della query per retrieval più ampio' },
                    { key: 'hyde' as const, label: 'HyDE', desc: 'Genera risposta ipotetica per migliorare il retrieval' },
                    { key: 'reranking' as const, label: 'Reranking', desc: 'Ri-ordina risultati per cosine similarity con la query' },
                    { key: 'queryCache' as const, label: 'Query Cache', desc: 'Cache LRU per query ripetute' },
                  ].map(({ key, label, desc }) => (
                    <li key={key} className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{label}</p>
                        <p className="text-xs text-slate-500">{desc}</p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={ragSettings[key]}
                        onClick={() => setRagSettings(s => ({ ...s, [key]: !s[key] }))}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${ragSettings[key] ? 'bg-indigo-600' : 'bg-slate-200'}`}
                      >
                        <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform ${ragSettings[key] ? 'translate-x-5' : 'translate-x-1'}`} style={{ marginTop: 2 }} />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
              <section>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Parametri retrieval & generazione</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-700">Top-K risultati</span>
                      <span className="font-medium text-slate-900">{ragSettings.topK}</span>
                    </div>
                    <input type="range" min={1} max={20} value={ragSettings.topK} onChange={e => setRagSettings(s => ({ ...s, topK: Number(e.target.value) }))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-700">Temperature LLM</span>
                      <span className="font-medium text-slate-900">{ragSettings.temperature}</span>
                    </div>
                    <input type="range" min={0} max={1} step={0.1} value={ragSettings.temperature} onChange={e => setRagSettings(s => ({ ...s, temperature: Number(e.target.value) }))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-700">Max token risposta</span>
                      <span className="font-medium text-slate-900">{ragSettings.maxTokens}</span>
                    </div>
                    <input type="range" min={256} max={2048} step={256} value={ragSettings.maxTokens} onChange={e => setRagSettings(s => ({ ...s, maxTokens: Number(e.target.value) }))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-700">Cache TTL (min)</span>
                      <span className="font-medium text-slate-900">{Math.round(ragSettings.cacheTtlSec / 60)}</span>
                    </div>
                    <input type="range" min={1} max={120} value={Math.round(ragSettings.cacheTtlSec / 60)} onChange={e => setRagSettings(s => ({ ...s, cacheTtlSec: Number(e.target.value) * 60 }))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-700">Cache dimensione</span>
                      <span className="font-medium text-slate-900">{ragSettings.cacheSize}</span>
                    </div>
                    <input type="range" min={100} max={2000} step={100} value={ragSettings.cacheSize} onChange={e => setRagSettings(s => ({ ...s, cacheSize: Number(e.target.value) }))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600" />
                  </div>
                </div>
              </section>
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => setRagSettings(DEFAULT_RAG_SETTINGS)}
                  className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-indigo-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                  Ripristina default
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Profilo */}
        {showProfile && messages.length === 0 && (
          <div className="mb-8 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-8 text-white">
              <div className="flex items-start gap-5">
                <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-3xl font-bold flex-shrink-0">
                  S
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Stefano De Sanctis</h2>
                  <p className="text-indigo-100 mt-1">Counselor - REICO n. 005</p>
                  <p className="text-indigo-200 text-sm mt-1">Direttore didattico Scuola di Counseling Aspic Perugia</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-5">
              <p className="text-slate-700 leading-relaxed">
                Counselor dal 1994 (Master in Gestalt counseling ASPIC). Co-fondatore dell'associazione REICO.
                Formatore, supervisore di counselor e operatori sociali. Attivo in ambito di counseling individuale e di coppia.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {['Gestalt', 'Counseling di coppia', 'Supervisione', 'Formazione', 'CNV'].map(tag => (
                  <span key={tag} className="px-3 py-1 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-slate-100">
                <p className="text-sm text-slate-500">
                  Questo assistente AI risponde basandosi sui contenuti del podcast
                  <strong className="text-slate-700"> "Libri Schermi Counseling"</strong> (46 episodi).
                  Puoi esplorare temi di counseling, tecniche, teorie e gestione della coppia.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Chat area */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col" style={{ minHeight: messages.length > 0 ? '70vh' : '40vh' }}>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <div className="text-4xl mb-3">🎙️</div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Chiedimi qualcosa sul podcast</h3>
                <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">
                  Posso rispondere su counseling, gestione della coppia, emozioni, tecniche come la Gestalt, la CNV, e molto altro.
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {suggestedQuestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => { setInput(q); inputRef.current?.focus(); }}
                      className="text-xs px-3 py-2 bg-indigo-50 text-indigo-700 rounded-full hover:bg-indigo-100 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-100 text-slate-800'
                }`}>
                  <div className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</div>
                  {msg.sources && msg.sources.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-xs font-semibold text-slate-600 mb-1.5">FONTI</p>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs font-mono text-indigo-700">
                        {msg.sources.map((s, j) => (
                          <span key={j}>[{j + 1}] {s.source}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 rounded-2xl px-4 py-3">
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span>Cerco nei contenuti del podcast...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="border-t border-slate-200 px-4 py-3 bg-slate-50/50">
            <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Chiedi qualcosa sul counseling..."
                className="flex-1 px-4 py-2.5 rounded-xl border border-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none text-sm transition-all"
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={!input.trim() || isLoading}
                className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl font-medium text-sm hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                Invia
              </button>
            </form>
            <p className="text-xs text-slate-400 mt-2 text-center">
              Le risposte sono generate dall'AI basandosi sui contenuti del podcast "Libri Schermi Counseling"
            </p>
          </div>
        </div>
      </div>

      <footer className="max-w-4xl mx-auto px-4 py-6 text-center">
        <p className="text-xs text-slate-400">
          Done by <a href="https://cv.marinuzzi.it" target="_blank" rel="noopener noreferrer" className="text-indigo-500 hover:text-indigo-700 underline">Francesco Marinuzzi</a>
        </p>
      </footer>
    </div>
  );
}
